import React, { useCallback, useEffect, useMemo, useState, Children, Fragment } from 'react';
import { Plus, Pencil, Trash2, X, AlertTriangle, Loader2, Check, FileText, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../store/authStore';
import { getAllContent, createContent, updateContent, deleteContent } from '../api/content';
interface ContentSection {
  id: string;
  title: string;
  slug: string;
  content?: string;
  sectionType: string;
  isPublished: boolean;
  orderIndex: number;
  parentId?: string | null;
  mediaUrl?: string | null;
  videoUrl?: string | null;
  videoDurationSeconds?: number | null;
  data?: any;
}
interface NestedSection extends ContentSection {
  children: NestedSection[];
}
const SECTION_TYPES = [{
  value: 'article',
  label: 'Article'
}, {
  value: 'faq',
  label: 'FAQ'
}, {
  value: 'video',
  label: 'Video'
}, {
  value: 'guide',
  label: 'Guide'
}];
const TYPE_STYLES: Record<string, {
  bg: string;
  text: string;
  border: string;
}> = {
  article: {
    bg: 'bg-[rgba(59,130,246,0.1)]',
    text: 'text-info',
    border: 'border-[rgba(59,130,246,0.25)]'
  },
  faq: {
    bg: 'bg-[rgba(107,33,232,0.12)]',
    text: 'text-accent',
    border: 'border-[rgba(107,33,232,0.3)]'
  },
  video: {
    bg: 'bg-[rgba(20,184,166,0.1)]',
    text: 'text-[#14B8A6]',
    border: 'border-[rgba(20,184,166,0.25)]'
  },
  guide: {
    bg: 'bg-[rgba(245,158,11,0.1)]',
    text: 'text-warning',
    border: 'border-[rgba(245,158,11,0.25)]'
  }
};
const SEED_SECTIONS = [{
  title: 'FAQs',
  slug: 'faqs',
  content: 'Frequently asked questions about Aurora Care and fetal monitoring.',
  sectionType: 'faq',
  orderIndex: 1,
  isPublished: true
}, {
  title: 'Understanding Fetal Heart Rate',
  slug: 'understanding-fetal-heart-rate',
  content: 'A normal fetal heart rate is between 110 and 160 BPM. Learn what variations mean.',
  sectionType: 'article',
  orderIndex: 2,
  isPublished: true
}, {
  title: 'Baby Movement & Position',
  slug: 'baby-movement',
  content: 'Feeling your baby move is a sign of good health. Track kicks and understand positions.',
  sectionType: 'article',
  orderIndex: 3,
  isPublished: true
}, {
  title: 'Vaccines & Immunization',
  slug: 'vaccines',
  content: 'Recommended immunization schedules and what to expect.',
  sectionType: 'guide',
  orderIndex: 4,
  isPublished: true
}, {
  title: 'Recommended Immunization (RI)',
  slug: 'recommended-immunization',
  content: 'The RI schedule by age and what each vaccine protects against.',
  sectionType: 'guide',
  orderIndex: 5,
  isPublished: true
}, {
  title: 'AEFI',
  slug: 'aefi',
  content: 'Adverse Events Following Immunization — what they are and when to seek help.',
  sectionType: 'article',
  orderIndex: 6,
  isPublished: true
}, {
  title: 'Baby Kicks & Fetal Kicks',
  slug: 'baby-kicks',
  content: 'How to track kicks and what kick patterns mean for baby health.',
  sectionType: 'article',
  orderIndex: 7,
  isPublished: true
}, {
  title: 'Fetal Hiccups',
  slug: 'fetal-hiccups',
  content: 'What fetal hiccups are, why they happen, and when to be concerned.',
  sectionType: 'article',
  orderIndex: 8,
  isPublished: true
}, {
  title: 'Pregnancy Stages',
  slug: 'pregnancy-stages',
  content: 'A month by month guide to what happens during each stage of pregnancy.',
  sectionType: 'guide',
  orderIndex: 9,
  isPublished: true
}, {
  title: 'Child State Before Vaccines',
  slug: 'child-state-before-vaccines',
  content: 'When to delay vaccination and what conditions to watch for beforehand.',
  sectionType: 'article',
  orderIndex: 10,
  isPublished: true
}];
function slugify(input: string) {
  return input.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
}
export function Content() {
  const {
    token
  } = useAuth();
  const [sections, setSections] = useState<ContentSection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editSection, setEditSection] = useState<ContentSection | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    section: ContentSection;
    childCount: number;
  } | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({
      message,
      type
    });
    setTimeout(() => setToast(null), 3000);
  };
  const fetchContent = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      const res = await getAllContent(token);
      console.log('[Content] /admin/content/all response:', res);
      let raw: ContentSection[] = [];
      if (Array.isArray(res)) {
        raw = res;
      } else if (Array.isArray(res?.content)) {
        raw = res.content;
      } else if (Array.isArray(res?.sections)) {
        raw = res.sections;
      } else if (Array.isArray(res?.data)) {
        raw = res.data;
      } else if (Array.isArray(res?.items)) {
        raw = res.items;
      } else if (Array.isArray(res?.results)) {
        raw = res.results;
      } else if (Array.isArray(res?.contentSections)) {
        raw = res.contentSections;
      } else if (res && typeof res === 'object') {
        const arrKey = Object.keys(res).find(k => Array.isArray((res as any)[k]));
        if (arrKey) raw = (res as any)[arrKey];
      }
      const normalized = raw.map((it: any) => ({
        id: it.id,
        title: it.title ?? '',
        slug: it.slug ?? '',
        content: it.content ?? it.body ?? '',
        sectionType: it.sectionType ?? it.section_type ?? it.type ?? 'article',
        isPublished: typeof it.isPublished === 'boolean' ? it.isPublished : typeof it.is_published === 'boolean' ? it.is_published : typeof it.published === 'boolean' ? it.published : false,
        orderIndex: it.orderIndex ?? it.order_index ?? it.order ?? 0,
        parentId: it.parentId ?? it.parent_id ?? null,
        mediaUrl: it.mediaUrl ?? it.media_url ?? null,
        videoUrl: it.videoUrl ?? it.video_url ?? null,
        videoDurationSeconds: it.videoDurationSeconds ?? it.video_duration_seconds ?? null,
        data: it.data ?? null
      }));
      setSections(normalized);
    } catch (err: any) {
      console.error('[Content] fetch failed:', err);
      setError(err.message || 'Failed to load content');
    } finally {
      setIsLoading(false);
    }
  }, [token]);
  useEffect(() => {
    fetchContent();
  }, [fetchContent]);
  // Build hierarchy
  const {
    hierarchy,
    topLevelSections,
    childCountMap
  } = useMemo(() => {
    const map = new Map<string, NestedSection>();
    const roots: NestedSection[] = [];
    const counts = new Map<string, number>();
    sections.forEach(it => {
      map.set(it.id, {
        ...it,
        children: []
      });
    });
    sections.forEach(it => {
      const node = map.get(it.id)!;
      if (it.parentId && map.has(it.parentId)) {
        map.get(it.parentId)!.children.push(node);
        counts.set(it.parentId, (counts.get(it.parentId) || 0) + 1);
      } else {
        roots.push(node);
      }
    });
    const sortFn = (a: NestedSection, b: NestedSection) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
    roots.sort(sortFn);
    roots.forEach(r => r.children.sort(sortFn));
    const tops = sections.filter(s => !s.parentId);
    return {
      hierarchy: roots,
      topLevelSections: tops,
      childCountMap: counts
    };
  }, [sections]);
  const togglePublished = async (section: ContentSection) => {
    if (!token) return;
    const newValue = !section.isPublished;
    // Optimistic update
    setSections(prev => prev.map(s => s.id === section.id ? {
      ...s,
      isPublished: newValue
    } : s));
    try {
      await updateContent(token, section.id, {
        isPublished: newValue
      });
      showToast(newValue ? 'Section published' : 'Marked as draft');
    } catch (err: any) {
      // Rollback
      setSections(prev => prev.map(s => s.id === section.id ? {
        ...s,
        isPublished: !newValue
      } : s));
      showToast(err.message || 'Failed to update', 'error');
    }
  };
  const renderRow = (section: NestedSection, depth = 0): React.ReactNode => {
    const styles = TYPE_STYLES[section.sectionType] || TYPE_STYLES.article;
    return <Fragment key={section.id} data-id="element-203">
        <tr className="h-[56px] border-b border-[#1E1E1E] hover:bg-[#1E1E1E] transition-colors" data-id="element-204">
          <td className="px-4" data-id="element-205">
            <div className="flex items-center" style={{
            paddingLeft: depth * 16
          }} data-id="element-206">
              {depth > 0 && <span aria-hidden className="inline-block w-3 h-[1px] bg-border mr-2" data-id="element-207" />}
              <span className="text-[14px] font-medium text-white" data-id="element-208">
                {section.title}
              </span>
            </div>
          </td>
          <td className="px-4" data-id="element-209">
            <span className="text-[13px] text-text-secondary font-mono" data-id="element-210">
              {section.slug}
            </span>
          </td>
          <td className="px-4" data-id="element-211">
            <span className={`px-2 py-0.5 rounded-badge ${styles.bg} ${styles.text} text-[12px] border ${styles.border} capitalize`} data-id="element-212">
              {section.sectionType}
            </span>
          </td>
          <td className="px-4" data-id="element-213">
            <button onClick={() => togglePublished(section)} className={`px-2 py-0.5 rounded-badge text-[12px] border transition-colors ${section.isPublished ? 'bg-[rgba(34,197,94,0.1)] text-success border-[rgba(34,197,94,0.25)] hover:bg-[rgba(34,197,94,0.2)]' : 'bg-[rgba(239,68,68,0.1)] text-danger border-[rgba(239,68,68,0.25)] hover:bg-[rgba(239,68,68,0.2)]'}`} title="Click to toggle" data-id="element-214">
              {section.isPublished ? 'Published' : 'Draft'}
            </button>
          </td>
          <td className="px-4 text-[13px] text-text-secondary" data-id="element-215">
            {section.orderIndex ?? 0}
          </td>
          <td className="px-4 text-[13px] text-text-secondary" data-id="element-216">
            {section.children.length > 0 ? section.children.length : '—'}
          </td>
          <td className="px-4" data-id="element-217">
            <div className="flex items-center gap-1" data-id="element-218">
              <button onClick={() => setEditSection(section)} className="px-2.5 h-7 rounded-btn flex items-center gap-1 text-[12px] hover:bg-border transition-colors text-text-secondary hover:text-white" data-id="element-219">
                <Pencil className="w-3.5 h-3.5" data-id="element-220" />
                Edit
              </button>
              <button onClick={() => setDeleteTarget({
              section,
              childCount: section.children.length
            })} className="px-2.5 h-7 rounded-btn flex items-center gap-1 text-[12px] hover:bg-[rgba(239,68,68,0.1)] transition-colors text-text-secondary hover:text-danger" data-id="element-221">
                <Trash2 className="w-3.5 h-3.5" data-id="element-222" />
                Delete
              </button>
            </div>
          </td>
        </tr>
        {section.children.map(child => renderRow(child, depth + 1))}
      </Fragment>;
  };
  const totalSections = sections.length;
  return <div className="relative min-h-full pb-20" data-id="element-223">
      <div className="flex justify-between items-center mb-6" data-id="element-224">
        <div data-id="element-225">
          <h1 className="text-[24px] font-semibold text-white" data-id="element-226">Content</h1>
          <p className="text-[14px] text-text-secondary mt-1" data-id="element-227">
            {isLoading ? 'Loading...' : `${totalSections} total section${totalSections === 1 ? '' : 's'}`}
          </p>
        </div>
        <button onClick={() => setIsAddOpen(true)} className="h-[36px] px-4 bg-accent text-white font-semibold rounded-btn flex items-center gap-2 hover:opacity-90 transition-opacity" data-id="element-228">
          <Plus className="w-4 h-4" data-id="element-229" />
          New Section
        </button>
      </div>

      {error && <div className="mb-4 p-3 bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] rounded-card text-danger text-[13px]" data-id="element-230">
          {error}
        </div>}

      <div className="bg-surface border border-border rounded-card overflow-hidden" data-id="element-231">
        <div className="overflow-x-auto" data-id="element-232">
          <table className="w-full text-left border-collapse" data-id="element-233">
            <thead data-id="element-234">
              <tr className="bg-surface-elevated border-b border-border h-[44px]" data-id="element-235">
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-236">
                  Title
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[200px]" data-id="element-237">
                  Slug
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[110px]" data-id="element-238">
                  Type
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[120px]" data-id="element-239">
                  Published
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[80px]" data-id="element-240">
                  Order
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[90px]" data-id="element-241">
                  Children
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[170px]" data-id="element-242">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-243">
              {isLoading ? <SkeletonRows data-id="element-244" /> : hierarchy.length === 0 ? <tr data-id="element-245">
                  <td colSpan={7} className="h-[280px] text-center" data-id="element-246">
                    <div className="flex flex-col items-center justify-center gap-4" data-id="element-247">
                      <div className="w-16 h-16 rounded-full bg-surface-elevated border border-border flex items-center justify-center" data-id="element-248">
                        <FileText className="w-7 h-7 text-text-tertiary" data-id="element-249" />
                      </div>
                      <div data-id="element-250">
                        <p className="text-[14px] text-white font-medium mb-1" data-id="element-251">
                          No content sections yet
                        </p>
                        <p className="text-[13px] text-text-secondary" data-id="element-252">
                          Click <span className="text-white" data-id="element-253">New Section</span>{' '}
                          to create your first one.
                        </p>
                      </div>
                    </div>
                  </td>
                </tr> : hierarchy.map(s => renderRow(s))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Seed Default Content - only visible when table is empty */}
      {!isLoading && totalSections === 0 && <SeedPanel onComplete={() => {
      fetchContent();
      showToast('Default content seeded');
    }} showToast={showToast} data-id="element-254" />}

      {/* Toast */}
      {toast && <div className={`fixed top-6 right-6 px-4 py-3 rounded-badge shadow-lg flex items-center gap-2 z-50 ${toast.type === 'success' ? 'bg-success text-white' : 'bg-danger text-white'}`} data-id="element-255">
          {toast.type === 'success' ? <Check className="w-4 h-4" data-id="element-256" /> : <AlertTriangle className="w-4 h-4" data-id="element-257" />}
          <span className="text-[14px] font-medium" data-id="element-258">{toast.message}</span>
        </div>}

      {/* Create Modal */}
      <ContentFormModal isOpen={isAddOpen} section={null} topLevelSections={topLevelSections} onClose={() => setIsAddOpen(false)} onSuccess={() => {
      setIsAddOpen(false);
      showToast('Section created');
      fetchContent();
    }} data-id="element-259" />

      {/* Edit Modal */}
      {editSection && <ContentFormModal isOpen={true} section={editSection} topLevelSections={topLevelSections.filter(s => s.id !== editSection.id)} onClose={() => setEditSection(null)} onSuccess={() => {
      setEditSection(null);
      showToast('Section updated');
      fetchContent();
    }} data-id="element-260" />}

      {/* Delete Confirmation */}
      {deleteTarget && <DeleteConfirmDialog section={deleteTarget.section} childCount={deleteTarget.childCount} onClose={() => setDeleteTarget(null)} onConfirm={async () => {
      if (!token) return;
      try {
        await deleteContent(token, deleteTarget.section.id);
        setSections(prev => prev.filter(s => s.id !== deleteTarget.section.id));
        setDeleteTarget(null);
        showToast('Section deleted');
      } catch (err: any) {
        showToast(err.message || 'Failed to delete', 'error');
      }
    }} data-id="element-261" />}
    </div>;
}
// --- Skeleton ---
function SkeletonRows() {
  return <>
      {Array.from({
      length: 5
    }).map((_, i) => <tr key={i} className="h-[56px] border-b border-[#1E1E1E]" data-id="element-262">
          <td className="px-4" data-id="element-263">
            <div className="h-3 w-48 bg-surface-elevated rounded animate-pulse" data-id="element-264" />
          </td>
          <td className="px-4" data-id="element-265">
            <div className="h-3 w-32 bg-surface-elevated rounded animate-pulse" data-id="element-266" />
          </td>
          <td className="px-4" data-id="element-267">
            <div className="h-5 w-16 bg-surface-elevated rounded-badge animate-pulse" data-id="element-268" />
          </td>
          <td className="px-4" data-id="element-269">
            <div className="h-5 w-20 bg-surface-elevated rounded-badge animate-pulse" data-id="element-270" />
          </td>
          <td className="px-4" data-id="element-271">
            <div className="h-3 w-6 bg-surface-elevated rounded animate-pulse" data-id="element-272" />
          </td>
          <td className="px-4" data-id="element-273">
            <div className="h-3 w-6 bg-surface-elevated rounded animate-pulse" data-id="element-274" />
          </td>
          <td className="px-4" data-id="element-275">
            <div className="h-7 w-24 bg-surface-elevated rounded-btn animate-pulse" data-id="element-276" />
          </td>
        </tr>)}
    </>;
}
// --- Seed Panel ---
function SeedPanel({
  onComplete,
  showToast
}: {
  onComplete: () => void;
  showToast: (msg: string, type?: 'success' | 'error') => void;
}) {
  const {
    token
  } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [progress, setProgress] = useState(0);
  const [failedAt, setFailedAt] = useState<number | null>(null);
  const [failedError, setFailedError] = useState('');
  const runSeed = async (startFrom = 0) => {
    if (!token) return;
    setIsSeeding(true);
    setFailedAt(null);
    setFailedError('');
    for (let i = startFrom; i < SEED_SECTIONS.length; i++) {
      setProgress(i);
      try {
        await createContent(token, SEED_SECTIONS[i]);
      } catch (err: any) {
        setFailedAt(i);
        setFailedError(err.message || 'Unknown error');
        setIsSeeding(false);
        return;
      }
    }
    setProgress(SEED_SECTIONS.length);
    setIsSeeding(false);
    setIsOpen(false);
    onComplete();
  };
  return <div className="mt-6 bg-surface border border-border rounded-card overflow-hidden" data-id="element-277">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-[#1E1E1E] transition-colors" data-id="element-278">
        <div className="flex items-center gap-3" data-id="element-279">
          <div className="w-9 h-9 rounded-full bg-[rgba(107,33,232,0.1)] flex items-center justify-center" data-id="element-280">
            <Sparkles className="w-4 h-4 text-accent" data-id="element-281" />
          </div>
          <div className="text-left" data-id="element-282">
            <p className="text-[14px] font-medium text-white" data-id="element-283">
              Seed Default Content
            </p>
            <p className="text-[12px] text-text-secondary" data-id="element-284">
              Quickly populate the panel with 10 starter sections
            </p>
          </div>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 text-text-secondary" data-id="element-285" /> : <ChevronDown className="w-4 h-4 text-text-secondary" data-id="element-286" />}
      </button>

      {isOpen && <div className="px-5 pb-5 pt-1 border-t border-border" data-id="element-287">
          <p className="text-[13px] text-text-secondary mt-3 mb-4" data-id="element-288">
            This will create {SEED_SECTIONS.length} top-level sections covering
            common pregnancy and immunization topics. You can edit or delete any
            of them later.
          </p>

          {isSeeding && <div className="mb-4 p-3 bg-background border border-border rounded-input" data-id="element-289">
              <div className="flex items-center gap-2 mb-2" data-id="element-290">
                <Loader2 className="w-4 h-4 animate-spin text-accent" data-id="element-291" />
                <span className="text-[13px] text-white" data-id="element-292">
                  Seeding {progress + 1}/{SEED_SECTIONS.length}...
                </span>
              </div>
              <div className="h-1.5 bg-border rounded-full overflow-hidden" data-id="element-293">
                <div className="h-full bg-accent transition-all duration-300" style={{
            width: `${(progress + 1) / SEED_SECTIONS.length * 100}%`
          }} data-id="element-294" />
              </div>
              <p className="text-[12px] text-text-secondary mt-2" data-id="element-295">
                Creating: {SEED_SECTIONS[progress]?.title}
              </p>
            </div>}

          {failedAt !== null && <div className="mb-4 p-3 bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] rounded-input" data-id="element-296">
              <p className="text-[13px] text-danger font-medium mb-1" data-id="element-297">
                Failed at item {failedAt + 1}: {SEED_SECTIONS[failedAt].title}
              </p>
              <p className="text-[12px] text-text-secondary mb-3" data-id="element-298">
                {failedError}
              </p>
              <button onClick={() => runSeed(failedAt)} className="px-3 h-8 bg-surface border border-border rounded-btn text-[12px] text-white hover:bg-border transition-colors" data-id="element-299">
                Retry from this item
              </button>
            </div>}

          {!isSeeding && failedAt === null && <button onClick={() => runSeed(0)} className="h-[36px] px-4 bg-accent text-white font-semibold rounded-btn flex items-center gap-2 hover:opacity-90 transition-opacity" data-id="element-300">
              <Sparkles className="w-4 h-4" data-id="element-301" />
              Seed Default Content
            </button>}
        </div>}
    </div>;
}
// --- Form Modal ---
function ContentFormModal({
  isOpen,
  section,
  topLevelSections,
  onClose,
  onSuccess
}: {
  isOpen: boolean;
  section: ContentSection | null;
  topLevelSections: ContentSection[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const {
    token
  } = useAuth();
  const isEdit = !!section;
  // Shared fields
  const [title, setTitle] = useState(section?.title || '');
  const [slug, setSlug] = useState(section?.slug || '');
  const [slugTouched, setSlugTouched] = useState(!!section?.slug);
  const [sectionType, setSectionType] = useState(section?.sectionType || 'article');
  const [parentId, setParentId] = useState<string>(section?.parentId || '');
  const [orderIndex, setOrderIndex] = useState<number>(section?.orderIndex ?? 0);
  const [isPublished, setIsPublished] = useState<boolean>(section ? section.isPublished : true);
  const [mediaUrl, setMediaUrl] = useState<string>(section?.mediaUrl || '');
  // Type-specific fields
  const [body, setBody] = useState<string>(section?.content || '');
  const [videoUrl, setVideoUrl] = useState<string>(section?.videoUrl || '');
  const [videoDuration, setVideoDuration] = useState<string>(section?.videoDurationSeconds != null ? String(section.videoDurationSeconds) : '');
  const [faqs, setFaqs] = useState<{
    q: string;
    a: string;
  }[]>(() => {
    const d = section?.data;
    if (d && Array.isArray(d.faqs)) return d.faqs;
    if (Array.isArray(d)) return d;
    return [{
      q: '',
      a: ''
    }];
  });
  const [steps, setSteps] = useState<{
    heading: string;
    body: string;
    image: string;
  }[]>(() => {
    const d = section?.data;
    if (d && Array.isArray(d.steps)) return d.steps;
    if (Array.isArray(d)) return d;
    return [{
      heading: '',
      body: '',
      image: ''
    }];
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!slugTouched && !isEdit) {
      setSlug(slugify(title));
    }
  }, [title, slugTouched, isEdit]);
  if (!isOpen) return null;
  const isYouTube = (url: string) => /(?:youtube\.com|youtu\.be)/i.test(url);
  const isVimeo = (url: string) => /vimeo\.com/i.test(url);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    // Type-specific validation
    if (sectionType === 'video' && !videoUrl.trim()) {
      setError('Video URL is required for video sections.');
      return;
    }
    if (sectionType === 'faq') {
      const cleaned = faqs.filter(f => f.q.trim() && f.a.trim());
      if (cleaned.length === 0) {
        setError('Add at least one question and answer.');
        return;
      }
    }
    if (sectionType === 'guide') {
      const cleaned = steps.filter(s => s.heading.trim() || s.body.trim());
      if (cleaned.length === 0) {
        setError('Add at least one step.');
        return;
      }
    }
    setIsLoading(true);
    setError('');
    // Build payload
    const basePayload: Record<string, any> = {
      title,
      slug,
      sectionType,
      parentId: parentId || null,
      orderIndex,
      isPublished,
      mediaUrl: mediaUrl.trim() || null
    };
    if (sectionType === 'article') {
      basePayload.content = body;
      basePayload.data = null;
      basePayload.videoUrl = null;
      basePayload.videoDurationSeconds = null;
    } else if (sectionType === 'video') {
      basePayload.content = body;
      basePayload.videoUrl = videoUrl.trim();
      basePayload.videoDurationSeconds = videoDuration ? Number(videoDuration) : null;
      basePayload.data = null;
    } else if (sectionType === 'faq') {
      basePayload.content = body;
      basePayload.data = {
        faqs: faqs.filter(f => f.q.trim() && f.a.trim())
      };
      basePayload.videoUrl = null;
      basePayload.videoDurationSeconds = null;
    } else if (sectionType === 'guide') {
      basePayload.content = body;
      basePayload.data = {
        steps: steps.filter(s => s.heading.trim() || s.body.trim()).map(s => ({
          heading: s.heading.trim(),
          body: s.body.trim(),
          image: s.image.trim() || null
        }))
      };
      basePayload.videoUrl = null;
      basePayload.videoDurationSeconds = null;
    }
    try {
      console.log('[Content] submitting payload:', basePayload);
      let response;
      if (isEdit && section) {
        response = await updateContent(token, section.id, basePayload);
      } else {
        response = await createContent(token, basePayload);
      }
      console.log('[Content] server response:', response);
      // Quick verification: did `data` come back?
      const returnedData = response?.data ?? response?.section?.data ?? response?.content?.data;
      if ((sectionType === 'faq' || sectionType === 'guide') && basePayload.data && !returnedData) {
        console.warn('[Content] WARNING: sent `data` payload but server did not return it. ' + 'The backend may not be persisting the `data` JSON column. ' + 'Sent:', basePayload.data, 'Got back:', response);
      }
      onSuccess();
    } catch (err: any) {
      console.error('[Content] submit failed:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-302">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-[4px]" onClick={onClose} data-id="element-303" />
      <div className="relative w-full max-w-[640px] bg-surface border border-border rounded-card p-8 shadow-2xl max-h-[90vh] overflow-y-auto" data-id="element-304">
        <button type="button" onClick={onClose} className="absolute top-6 right-6 text-text-secondary hover:text-white transition-colors" data-id="element-305">
          <X className="w-5 h-5" data-id="element-306" />
        </button>

        <h2 className="text-[18px] font-semibold text-white mb-6" data-id="element-307">
          {isEdit ? 'Edit Section' : 'New Section'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-308">
          {/* Title */}
          <div data-id="element-309">
            <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-310">
              Title <span className="text-danger" data-id="element-311">*</span>
            </label>
            <input type="text" required value={title} onChange={e => setTitle(e.target.value)} className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent" data-id="element-312" />
          </div>

          {/* Slug */}
          <div data-id="element-313">
            <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-314">
              Slug <span className="text-danger" data-id="element-315">*</span>
            </label>
            <input type="text" required value={slug} onChange={e => {
            setSlug(e.target.value);
            setSlugTouched(true);
          }} className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white font-mono focus:outline-none focus:border-accent" placeholder="auto-generated-from-title" data-id="element-316" />
            {isEdit && <p className="text-[12px] text-warning mt-1.5" data-id="element-317">
                ⚠ Changing the slug may break existing links.
              </p>}
          </div>

          {/* Type + Order */}
          <div className="grid grid-cols-2 gap-4" data-id="element-318">
            <div data-id="element-319">
              <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-320">
                Section Type
              </label>
              <select value={sectionType} onChange={e => setSectionType(e.target.value)} className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent" data-id="element-321">
                {SECTION_TYPES.map(t => <option key={t.value} value={t.value} data-id="element-322">
                    {t.label}
                  </option>)}
              </select>
            </div>
            <div data-id="element-323">
              <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-324">
                Order Index
              </label>
              <input type="number" value={orderIndex} onChange={e => setOrderIndex(Number(e.target.value))} className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent" data-id="element-325" />
            </div>
          </div>

          {/* Parent */}
          <div data-id="element-326">
            <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-327">
              Parent Section (optional)
            </label>
            <select value={parentId} onChange={e => setParentId(e.target.value)} className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent" data-id="element-328">
              <option value="" data-id="element-329">None (top-level section)</option>
              {topLevelSections.map(s => <option key={s.id} value={s.id} data-id="element-330">
                  {s.title}
                </option>)}
            </select>
          </div>

          {/* Shared media URL (hero / thumbnail) */}
          <div data-id="element-331">
            <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-332">
              {sectionType === 'video' ? 'Thumbnail Image URL' : 'Hero Image URL'}{' '}
              <span className="text-text-tertiary text-[12px]" data-id="element-333">(optional)</span>
            </label>
            <input type="url" value={mediaUrl} onChange={e => setMediaUrl(e.target.value)} placeholder="https://..." className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent" data-id="element-334" />
          </div>

          {/* Type-specific section */}
          <div className="pt-2 border-t border-border" data-id="element-335" />

          {sectionType === 'article' && <div data-id="element-336">
              <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-337">
                Body
              </label>
              <textarea value={body} onChange={e => setBody(e.target.value)} rows={8} placeholder="Article body. Markdown supported." className="w-full bg-background border border-border rounded-input px-4 py-3 text-white focus:outline-none focus:border-accent resize-y" data-id="element-338" />
            </div>}

          {sectionType === 'video' && <>
              <div data-id="element-339">
                <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-340">
                  Video URL <span className="text-danger" data-id="element-341">*</span>
                </label>
                <input type="url" required value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..." className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent" data-id="element-342" />
                {videoUrl.trim() && <p className="text-[12px] mt-1.5" data-id="element-343">
                    {isYouTube(videoUrl) ? <span className="text-success" data-id="element-344">
                        ✓ Detected as YouTube
                      </span> : isVimeo(videoUrl) ? <span className="text-success" data-id="element-345">✓ Detected as Vimeo</span> : <span className="text-text-secondary" data-id="element-346">
                        Custom URL — patient app will need to handle this.
                      </span>}
                  </p>}
              </div>

              <div data-id="element-347">
                <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-348">
                  Duration (seconds){' '}
                  <span className="text-text-tertiary text-[12px]" data-id="element-349">
                    (optional)
                  </span>
                </label>
                <input type="number" min={0} value={videoDuration} onChange={e => setVideoDuration(e.target.value)} placeholder="e.g. 180" className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent" data-id="element-350" />
              </div>

              <div data-id="element-351">
                <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-352">
                  Description{' '}
                  <span className="text-text-tertiary text-[12px]" data-id="element-353">
                    (optional)
                  </span>
                </label>
                <textarea value={body} onChange={e => setBody(e.target.value)} rows={4} placeholder="Short summary shown alongside the video." className="w-full bg-background border border-border rounded-input px-4 py-3 text-white focus:outline-none focus:border-accent resize-y" data-id="element-354" />
              </div>
            </>}

          {sectionType === 'faq' && <>
              <div data-id="element-355">
                <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-356">
                  Intro{' '}
                  <span className="text-text-tertiary text-[12px]" data-id="element-357">
                    (optional)
                  </span>
                </label>
                <textarea value={body} onChange={e => setBody(e.target.value)} rows={3} placeholder="Brief intro shown above the FAQs." className="w-full bg-background border border-border rounded-input px-4 py-3 text-white focus:outline-none focus:border-accent resize-y" data-id="element-358" />
              </div>

              <div data-id="element-359">
                <div className="flex items-center justify-between mb-2" data-id="element-360">
                  <label className="text-[13px] text-text-secondary" data-id="element-361">
                    Questions & Answers
                  </label>
                  <span className="text-[12px] text-text-tertiary" data-id="element-362">
                    {faqs.length} item{faqs.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="space-y-3" data-id="element-363">
                  {faqs.map((faq, i) => <div key={i} className="bg-background border border-border rounded-input p-3 space-y-2" data-id="element-364">
                      <div className="flex items-start gap-2" data-id="element-365">
                        <span className="text-[12px] text-text-tertiary font-mono mt-3 w-6 flex-shrink-0" data-id="element-366">
                          {i + 1}.
                        </span>
                        <div className="flex-1 space-y-2" data-id="element-367">
                          <input type="text" value={faq.q} onChange={e => {
                      const next = [...faqs];
                      next[i] = {
                        ...next[i],
                        q: e.target.value
                      };
                      setFaqs(next);
                    }} placeholder="Question" className="w-full h-[36px] bg-surface border border-border rounded-input px-3 text-white text-[13px] focus:outline-none focus:border-accent" data-id="element-368" />
                          <textarea value={faq.a} onChange={e => {
                      const next = [...faqs];
                      next[i] = {
                        ...next[i],
                        a: e.target.value
                      };
                      setFaqs(next);
                    }} placeholder="Answer" rows={3} className="w-full bg-surface border border-border rounded-input px-3 py-2 text-white text-[13px] focus:outline-none focus:border-accent resize-y" data-id="element-369" />
                        </div>
                        {faqs.length > 1 && <button type="button" onClick={() => setFaqs(faqs.filter((_, idx) => idx !== i))} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-[rgba(239,68,68,0.1)] text-text-tertiary hover:text-danger transition-colors flex-shrink-0 mt-2" title="Remove" data-id="element-370">
                            <X className="w-4 h-4" data-id="element-371" />
                          </button>}
                      </div>
                    </div>)}
                </div>
                <button type="button" onClick={() => setFaqs([...faqs, {
              q: '',
              a: ''
            }])} className="mt-3 px-3 h-[34px] bg-surface border border-border text-white rounded-btn text-[13px] hover:bg-border transition-colors flex items-center gap-2" data-id="element-372">
                  <Plus className="w-3.5 h-3.5" data-id="element-373" />
                  Add Question
                </button>
              </div>
            </>}

          {sectionType === 'guide' && <>
              <div data-id="element-374">
                <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-375">
                  Intro{' '}
                  <span className="text-text-tertiary text-[12px]" data-id="element-376">
                    (optional)
                  </span>
                </label>
                <textarea value={body} onChange={e => setBody(e.target.value)} rows={3} placeholder="Brief intro shown above the steps." className="w-full bg-background border border-border rounded-input px-4 py-3 text-white focus:outline-none focus:border-accent resize-y" data-id="element-377" />
              </div>

              <div data-id="element-378">
                <div className="flex items-center justify-between mb-2" data-id="element-379">
                  <label className="text-[13px] text-text-secondary" data-id="element-380">
                    Steps
                  </label>
                  <span className="text-[12px] text-text-tertiary" data-id="element-381">
                    {steps.length} step{steps.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="space-y-3" data-id="element-382">
                  {steps.map((step, i) => <div key={i} className="bg-background border border-border rounded-input p-3 space-y-2" data-id="element-383">
                      <div className="flex items-start gap-2" data-id="element-384">
                        <span className="text-[12px] text-text-tertiary font-mono mt-3 w-6 flex-shrink-0" data-id="element-385">
                          {i + 1}.
                        </span>
                        <div className="flex-1 space-y-2" data-id="element-386">
                          <input type="text" value={step.heading} onChange={e => {
                      const next = [...steps];
                      next[i] = {
                        ...next[i],
                        heading: e.target.value
                      };
                      setSteps(next);
                    }} placeholder="Step heading" className="w-full h-[36px] bg-surface border border-border rounded-input px-3 text-white text-[13px] focus:outline-none focus:border-accent" data-id="element-387" />
                          <textarea value={step.body} onChange={e => {
                      const next = [...steps];
                      next[i] = {
                        ...next[i],
                        body: e.target.value
                      };
                      setSteps(next);
                    }} placeholder="Step description" rows={3} className="w-full bg-surface border border-border rounded-input px-3 py-2 text-white text-[13px] focus:outline-none focus:border-accent resize-y" data-id="element-388" />
                          <input type="url" value={step.image} onChange={e => {
                      const next = [...steps];
                      next[i] = {
                        ...next[i],
                        image: e.target.value
                      };
                      setSteps(next);
                    }} placeholder="Image URL (optional)" className="w-full h-[36px] bg-surface border border-border rounded-input px-3 text-white text-[13px] font-mono focus:outline-none focus:border-accent" data-id="element-389" />
                        </div>
                        {steps.length > 1 && <button type="button" onClick={() => setSteps(steps.filter((_, idx) => idx !== i))} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-[rgba(239,68,68,0.1)] text-text-tertiary hover:text-danger transition-colors flex-shrink-0 mt-2" title="Remove" data-id="element-390">
                            <X className="w-4 h-4" data-id="element-391" />
                          </button>}
                      </div>
                    </div>)}
                </div>
                <button type="button" onClick={() => setSteps([...steps, {
              heading: '',
              body: '',
              image: ''
            }])} className="mt-3 px-3 h-[34px] bg-surface border border-border text-white rounded-btn text-[13px] hover:bg-border transition-colors flex items-center gap-2" data-id="element-392">
                  <Plus className="w-3.5 h-3.5" data-id="element-393" />
                  Add Step
                </button>
              </div>
            </>}

          {/* Published toggle */}
          <div className="flex items-center justify-between bg-background border border-border rounded-input px-4 py-3" data-id="element-394">
            <div data-id="element-395">
              <p className="text-[14px] text-white" data-id="element-396">Published</p>
              <p className="text-[12px] text-text-secondary" data-id="element-397">
                Visible to users when on
              </p>
            </div>
            <button type="button" onClick={() => setIsPublished(!isPublished)} className={`relative w-[44px] h-[24px] rounded-full transition-colors ${isPublished ? 'bg-accent' : 'bg-border'}`} aria-label="Toggle published" data-id="element-398">
              <div className={`absolute top-[2px] w-[20px] h-[20px] rounded-full bg-white transition-transform ${isPublished ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} data-id="element-399" />
            </button>
          </div>

          {error && <div className="p-3 bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] rounded-input" data-id="element-400">
              <p className="text-danger text-[13px]" data-id="element-401">{error}</p>
            </div>}

          <button type="submit" disabled={isLoading} className="w-full h-[44px] bg-accent text-white font-semibold rounded-btn mt-2 flex items-center justify-center hover:opacity-90 disabled:opacity-50" data-id="element-402">
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-403" /> : isEdit ? 'Save Changes' : 'Create Section'}
          </button>
        </form>
      </div>
    </div>;
}
// --- Delete Confirm Dialog ---
function DeleteConfirmDialog({
  section,
  childCount,
  onClose,
  onConfirm
}: {
  section: ContentSection;
  childCount: number;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [isConfirming, setIsConfirming] = useState(false);
  const handleConfirm = async () => {
    setIsConfirming(true);
    await onConfirm();
    setIsConfirming(false);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-404">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-[4px]" onClick={onClose} data-id="element-405" />
      <div className="relative w-full max-w-[420px] bg-surface border border-border rounded-card p-6 shadow-2xl" data-id="element-406">
        <div className="flex items-start gap-3 mb-4" data-id="element-407">
          <div className="w-9 h-9 rounded-full bg-[rgba(239,68,68,0.1)] flex items-center justify-center flex-shrink-0" data-id="element-408">
            <Trash2 className="w-4 h-4 text-danger" data-id="element-409" />
          </div>
          <div data-id="element-410">
            <h2 className="text-[15px] font-semibold text-white mb-1" data-id="element-411">
              Delete this section?
            </h2>
            <p className="text-[13px] text-text-secondary" data-id="element-412">
              Are you sure you want to delete{' '}
              <strong className="text-white" data-id="element-413">{section.title}</strong>? This
              cannot be undone.
            </p>
          </div>
        </div>

        {childCount > 0 && <div className="mb-4 p-3 bg-[rgba(245,158,11,0.1)] border border-[rgba(245,158,11,0.3)] rounded-input" data-id="element-414">
            <p className="text-[12px] text-warning leading-relaxed" data-id="element-415">
              ⚠ This section has {childCount} child section
              {childCount === 1 ? '' : 's'}. Deleting the parent will not
              automatically delete children but they will become orphaned.
            </p>
          </div>}

        <div className="flex gap-3 justify-end" data-id="element-416">
          <button onClick={onClose} disabled={isConfirming} className="px-4 h-[36px] bg-surface border border-border text-white rounded-btn font-medium text-[13px] hover:bg-border transition-colors disabled:opacity-50" data-id="element-417">
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={isConfirming} className="px-4 h-[36px] bg-danger text-white rounded-btn font-medium text-[13px] hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2" data-id="element-418">
            {isConfirming && <Loader2 className="w-3.5 h-3.5 animate-spin" data-id="element-419" />}
            Delete
          </button>
        </div>
      </div>
    </div>;
}